// The admin portal's auth state machine, extracted from the App component so
// the component tree stays a thin composition of screens + routes.
import { useCallback, useEffect, useRef, useState } from "react";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import type { AdminConfig } from "../lib/config";
import { getAdminSupabaseClient } from "../lib/supabase";
import { readJwtClaims, hasAdminRoleClaim, describeRoleClaim } from "../lib/claims";
import { isRecoveryHash } from "@pgs-core/portal-lib/recovery";
import { createApiClient, type ApiClient } from "../lib/api";
import type { AdminUser } from "../lib/outlet-context";

export type AdminView = "loading" | "config-error" | "login" | "set-password" | "forbidden" | "admin";

export interface AdminAuth {
  view: AdminView;
  error: string;
  /** Authenticated API client; null when no API is configured. */
  api: ApiClient | null;
  user: AdminUser | null;
  environment: string | null;
  /**
   * True when the admin view was entered via the login form (rather than a
   * restored session) — the cue for the login-to-admin reveal animation.
   */
  enteredFromLogin: boolean;
  /** Email of the account a recovery (password-reset) link signed in. */
  recoveryEmail: string;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  completeSetPassword: (password: string) => Promise<void>;
  /** Change the signed-in admin's password; resolves to an error message, or null on success. */
  changePassword: (password: string) => Promise<string | null>;
}

export function useAdminAuth(config: AdminConfig): AdminAuth {
  const [view, setView] = useState<AdminView>("loading");
  const [error, setError] = useState("");
  const [user, setUser] = useState<AdminUser | null>(null);
  const [supabase, setSupabase] = useState<SupabaseClient | null>(null);
  const [environment, setEnvironment] = useState<string | null>(null);
  const [enteredFromLogin, setEnteredFromLogin] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const signingInFromLogin = useRef(false);
  // Password-reset links land with recovery tokens in the hash — hold the
  // admin on the set-password screen instead of entering the console.
  const recoveryRef = useRef(isRecoveryHash(window.location.hash));
  // One client for the app's lifetime — its identity never changes, so pages
  // and effects can depend on it safely. The hook pushes every new bearer
  // token into its token store; API calls read it at request time, so a
  // refresh never touches React state or retriggers page effects.
  const [api] = useState<ApiClient | null>(() =>
    config.ok && config.apiBaseUrl ? createApiClient({ baseUrl: config.apiBaseUrl }) : null
  );
  // Signed-in flag: Supabase re-emits auth events whenever the tab regains
  // focus (SIGNED_IN with the same token, or TOKEN_REFRESHED with a new one);
  // once signed in those only need the token pushed into the api client.
  const signedInRef = useRef(false);

  const resetSession = useCallback(() => {
    signedInRef.current = false;
    api?.setToken("");
    setUser(null);
    setEnvironment(null);
    setEnteredFromLogin(false);
  }, [api]);

  const showForbidden = useCallback((message: string) => {
    resetSession();
    setError(message);
    setView("forbidden");
  }, [resetSession]);

  const acceptSession = useCallback(async (newSession: Session) => {
    // Already signed in (tab-focus SIGNED_IN re-emits, or a TOKEN_REFRESHED
    // after the browser throttled a background tab): push the token into the
    // api client — nothing else changes, so no React state is touched and no
    // page effect re-runs.
    if (signedInRef.current) {
      api?.setToken(newSession.access_token);
      return;
    }

    let claims;
    try {
      claims = readJwtClaims(newSession.access_token);
    } catch (err) {
      showForbidden(`Unable to read JWT claims: ${err instanceof Error ? err.message : err}`);
      return;
    }

    if (!hasAdminRoleClaim(claims)) {
      showForbidden(`JWT role claim is "${describeRoleClaim(claims)}", not "admin".`);
      return;
    }

    const newUser: AdminUser = {
      id: typeof claims.sub === "string" ? claims.sub : "",
      email: typeof claims.email === "string" ? claims.email : "",
    };

    if (api) {
      api.setToken(newSession.access_token);
      try {
        const me = await api.request<{ environment?: string | null }>("/admin/me");
        setEnvironment(me?.environment ?? null);
      } catch (err) {
        showForbidden(`API admin verification failed: ${err instanceof Error ? err.message : err}`);
        return;
      }
    }

    signedInRef.current = true;
    setUser(newUser);
    // A sign-in from the login form (as opposed to a restored session) plays
    // the shrink-to-circle-then-zoom reveal on entry.
    setEnteredFromLogin(signingInFromLogin.current);
    signingInFromLogin.current = false;
    setView("admin");
  }, [config, api, showForbidden]);

  useEffect(() => {
    if (!config.ok) {
      setError(config.message);
      setView("config-error");
      return;
    }

    const client = getAdminSupabaseClient({
      supabaseUrl: config.supabaseUrl,
      publishableKey: config.publishableKey,
    });
    setSupabase(client);

    // Save-resilience: if an API call 401s (the token lapsed while the tab was
    // backgrounded, before autoRefresh caught up), refresh the Supabase
    // session and hand back the fresh token for a retry. The api client
    // adopts the returned token itself.
    api?.setAuthRefreshHandler(async () => {
      const { data, error: refreshError } = await client.auth.refreshSession();
      if (refreshError || !data.session) return null;
      return data.session.access_token;
    });

    const showSetPassword = (recoverySession: { user?: { email?: string } | null }) => {
      recoveryRef.current = true;
      setRecoveryEmail(recoverySession.user?.email ?? "");
      setError("");
      setView("set-password");
    };

    (async () => {
      const { data, error: sessionError } = await client.auth.getSession();
      if (sessionError) {
        setError(sessionError.message);
        setView("login");
        return;
      }
      if (data.session) {
        if (recoveryRef.current) {
          showSetPassword(data.session);
        } else {
          await acceptSession(data.session);
        }
      } else {
        recoveryRef.current = false;
        setView("login");
      }
    })();

    const { data: { subscription } } = client.auth.onAuthStateChange(async (event, newSession) => {
      if (event === "PASSWORD_RECOVERY" && newSession) {
        showSetPassword(newSession);
        return;
      }
      // While the set-password screen is up, ignore the SIGNED_IN/USER_UPDATED
      // events Supabase fires around it — completeSetPassword moves us on.
      if (recoveryRef.current) return;
      if (newSession) {
        await acceptSession(newSession);
      } else {
        resetSession();
        setView("login");
      }
    });

    return () => subscription.unsubscribe();
    // Bootstrap runs once; config is a module-scope constant in the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!supabase) return;
    setError("");
    signingInFromLogin.current = true;
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      signingInFromLogin.current = false;
      setError(signInError.message);
    }
  }, [supabase]);

  const completeSetPassword = useCallback(async (password: string) => {
    if (!supabase) return;
    setError("");
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setError(updateError.message);
      return;
    }
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    recoveryRef.current = false;
    setRecoveryEmail("");
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      await acceptSession(data.session);
    } else {
      setView("login");
    }
  }, [supabase, acceptSession]);

  const signOut = useCallback(async () => {
    if (supabase) await supabase.auth.signOut();
    resetSession();
    setView("login");
  }, [supabase, resetSession]);

  const changePassword = useCallback(async (password: string): Promise<string | null> => {
    if (!supabase) return "Authentication is not configured.";
    const { error: updateError } = await supabase.auth.updateUser({ password });
    return updateError ? updateError.message : null;
  }, [supabase]);

  return { view, error, api, user, environment, enteredFromLogin, recoveryEmail, signIn, signOut, completeSetPassword, changePassword };
}
